import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import config from './ConfigPath';
import { Mat, cvtColor, resize, INTER_LINEAR, Size, GaussianBlur, COLOR_BGR2RGBA, COLOR_GRAY2RGBA, COLOR_GRAY2BGR, COLOR_BGRA2GRAY, CV_8U, imread, minMaxLoc, Rect, Point, circle, LINE_AA, CV_16U, Scalar, float } from '@techstark/opencv-js';
import FrameProcessorPressure from "components/Modals/PatientModal/utils/FrameProcessorPressure";
import PressureRenderOptions from "./PressureRenderOptions";
import zoneProcessor, { ZoneNames } from "components/Modals/PatientModal/utils/zoneProcessor";
import Line from 'components/Modals/PatientModal/utils/Line'
import { Box, Button, Typography, Stack, FormControlLabel, Checkbox, RadioGroup, Radio } from '@mui/material';
import ChartsWithMUIX from "../components/Modals/PatientModal/Chart";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import type { MouseEvent as ReactMouseEvent } from 'react';
import cv from "@techstark/opencv-js";
import { useViewerStore } from "context/ViewerContext";


export interface PressureViewerHandler {
    getCalibFrameArray(): Mat[];
    detectFoot(): boolean;
    setZones: (processedZones: zoneProcessor) => void;
    setFrameTimes: (times: number[]) => void;
    setCalibFrameArray: (frames: Mat[]) => void;
    setCalibMaxFrame: (frame: Mat) => void;
    setRenderOptions: (options: PressureRenderOptions) => void;
    Play: () => void;
    Pause: () => void;
    setPressureData: (ROI: Rect, processCOPs: Point[], axis: Line, width: Line, heel: Line) => void;
    setCropView: () => void;
    getZoneCenter: (zname: ZoneNames) => Point;
    getHeelCenterOfPressure: () => Point;
    getZonePressure: (zname: ZoneNames) => number;
    getZoneCenterOfPressureMinThr: (zname: ZoneNames, thr: number) => Point;
    setProcessData: (sensorwidth: number, sensorheight: number, fp: FrameProcessorPressure) => void;
    setStaticView: () => void;
    setDynamicView: () => void;
    updateChartSeries: () => any;
    getRealSizedPoint: (framePoint: Point) => Point;
    getPrintBitmap: (width: number, height: number, pmode: string) => Promise<HTMLCanvasElement | null>;
}

export interface PressureViewerProps {
    mode: Mode;
    imgData: ImageMatInput;
    footLabel?: string;
    chartVis?: boolean;
    legendVis?: boolean;
    /**
     * Mobile layout: stack the foot image above the chart instead of side by
     * side, and size both from explicit heights so the viewer can live inside
     * an auto-height column (desktop keeps the flex-filled row layout).
     */
    stackChart?: boolean;
    editMode?: 'none' | 'dimensions' | 'zones';
    onGeometryChange?: (payload:
        | { kind: 'dimensions'; axis: Line; width: Line; heel: Line }
        | { kind: 'zones'; controlPoints: Point[] }
    ) => void;
}

type Mode = 'online' | 'viewer' | 'edit' | 'printStatic' | 'printDynamic' | 'static' | 'dynamic';
type ImageMatInput = Mat | ImageData | null;

const PressureViewerComponent = forwardRef<PressureViewerHandler, PressureViewerProps>((
    { mode, imgData, footLabel, chartVis, legendVis, stackChart = false, editMode = 'none', onGeometryChange }, ref
) => {

    const [pressureLabel, setPressureLabel] = useState<string>("1.1 (Kg/CM2)");
    const [isDynamicView, setIsDynamicView] = useState<boolean>(false);
    const [pauseFrames, setPauseFrames] = React.useState(false);
    const [sliderMaxValue, setSliderMaxValue] = React.useState(100);
    const [sliderValue, setSliderValue] = React.useState(0);
    const [timeLabel, setTimeLabel] = React.useState('0 ms');
    const printMode = mode === 'printStatic' || mode === 'printDynamic';
    const [currentChart, setCurrentChart] = React.useState<string>(printMode ? "zones" : "force");

    const colorMapCustom = useRef<[number, number, number][] | null>(null);
    const colorMapPrint = useRef<[number, number, number][] | null>(null);
    const currentFrameIndex = useRef<number>(0);
    const frameTimes = useRef<number[] | null>(null);
    const calibFrameArray = useRef<Mat[] | null>(null);
    const calibFrameCOPs = useRef<Point[] | null>(null);
    const calibMaxFrame = useRef<Mat | null>(null);

    const frameProcessor = useRef<FrameProcessorPressure>(new FrameProcessorPressure());
    const renderOptions = useRef<PressureRenderOptions>(new PressureRenderOptions());
    const renderROI = useRef<Rect>(new Rect(0, 0, 44, 52));
    const renderRectangle = useRef<Rect>(new Rect(0, 0, 0, 0));
    const processROI = useRef<Rect>(new Rect(0, 0, 0, 0));
    const SSF = useRef<number>(10);
    const zones = useRef<zoneProcessor | null>(null);
    const axisLine = useRef<Line>(new Line());
    const widthLine = useRef<Line>(new Line());
    const heelLine = useRef<Line>(new Line());

    const sensorWidth = useRef<number>(-1);
    const sensorHeight = useRef<number>(-1);

    const currentBitmap = useRef<Mat | null>(null);
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

    const myCanvas = useRef(null);
    const { width, height } = useResizeObserver(myCanvas);

    const [chartTimes, setChartTimes] = useState<number[]>([]);
    const [chartData, setChartData] = useState<any>({});
    const [maxRenderValue, setMaxRenderValue] = useState<number>(600);
    const editLockScale = useRef<number | null>(null);
    const [cursor, setCursor] = useState<'default' | 'grab' | 'grabbing'>('default');

    const pressureToIntScale = 100;

    const ctrlpnt = useRef<Point[]>([]);

    useEffect(() => {
        setPressureLabel((maxRenderValue / pressureToIntScale).toFixed(1) + ' (Kg/CM2)');
        useViewerStore.setState({ pressureMaxLabel: (maxRenderValue / pressureToIntScale) })
    }, [maxRenderValue]);

    useEffect(() => {
        renderRectangle.current = GetZoomRectangle(processROI.current);
        render();
    }, [isDynamicView]);

    useImperativeHandle(ref, () => ({
        getCalibFrameArray: () => calibFrameArray.current ?? [],
        detectFoot,
        setZones,
        setFrameTimes,
        setCalibFrameArray,
        setCalibMaxFrame,
        setRenderOptions,
        Play,
        Pause,
        setPressureData,
        setCropView,
        getZoneCenter,
        getHeelCenterOfPressure,
        getZonePressure,
        getZoneCenterOfPressureMinThr,
        setProcessData,
        setStaticView,
        setDynamicView,
        updateChartSeries,
        getRealSizedPoint,
        getPrintBitmap
    }), []);

    useEffect(() => {
        if (mode === 'online') {
            renderOptions.current.setOnlineMode();
        }
        else { //if (mode === 'viewer')
            renderOptions.current.setViewerMode();
        }

        GenerateCustomColorMap().then((colorMap) => {
            colorMapCustom.current = colorMap;
            if (!isDynamicView) render();
        });

        if (mode !== 'online') {
            GenerateCustomColorMap('print').then((colormap) => {
                colorMapPrint.current = colormap;
            });
        }

        return () => {
            stopFrameTimer();

            colorMapCustom.current = null;
            colorMapPrint.current = null;
            frameTimes.current = null;
            calibFrameCOPs.current = null;
            calibFrameArray.current = null;
            calibMaxFrame.current = null;
            zones.current = null;
            currentBitmap.current?.delete();
            currentBitmap.current = null;
            myCanvas.current = null;
        };
    }, []);

    useEffect(() => {
        if (imgData) {
            renderOptions.current.FullView = true;
            setCalibFrameArray([imgData as cv.Mat]);
            render();
        }
    }, [imgData]);

    useEffect(() => {
        updateChartSeries();
    }, [currentChart])

    useEffect(() => {
        if (mode.startsWith('print'))
            setCurrentChart('zones');
    }, [mode])

    useEffect(() => {
        const canvas = myCanvas.current;
        if (!canvas || width === 0 || height === 0) return;

        // Set the canvas drawing surface resolution
        if (!printMode) renderRectangle.current = GetZoomRectangle(renderROI.current);
        if (editModeRef.current != 'none' || intervalIdRef.current === null) render();

    }, [width, height]);

    const handleSliderChange = (newValue: string) => {
        currentFrameIndex.current = parseInt(newValue);
        setTimeLabel(frameTimes.current ? frameTimes.current[currentFrameIndex.current] + (' ms') : "0 ms");
        setSliderValue(currentFrameIndex.current);
        render();
    };

    const editModeRef = useRef<'none' | 'dimensions' | 'zones'>(editMode);
    useEffect(() => {
        editModeRef.current = editMode;
        if (editModeRef.current != 'none') {

            if (editModeRef.current === 'dimensions') {
                renderOptions.current.Draw_AxisLine = true;
                renderOptions.current.Draw_WidthLine = true;
                renderOptions.current.Draw_HeelLine = true;

                renderOptions.current.Draw_ZonesOverlay = false;
            }
            else if (editModeRef.current == 'zones') {
                renderOptions.current.Draw_ZonesOverlay = true;

                renderOptions.current.Draw_AxisLine = false;
                renderOptions.current.Draw_WidthLine = false;
                renderOptions.current.Draw_HeelLine = false;
            }

            if (calibFrameArray.current && calibFrameArray.current?.length > 1)
                renderOptions.current.Render_MaxFrame = true;
        }
        else if (calibFrameArray.current && calibFrameArray.current?.length > 1) {
            renderOptions.current.Render_MaxFrame = false;
        }

        render();
    }, [editMode]);

    type DragTarget =
        | { type: 'axis', end: 'start' | 'end' }
        | { type: 'width', end: 'start' | 'end' }
        | { type: 'heel', end: 'start' | 'end' }
        | { type: 'zone', index: number };

    const dragRef = useRef<{ active: boolean; target?: DragTarget; lastX: number; lastY: number }>({ active: false, lastX: 0, lastY: 0 });

    const clampToROI = (p: Point) => {
        const r = processROI.current;
        return new Point(
            Math.min(Math.max(p.x, r.x), r.x + r.width),
            Math.min(Math.max(p.y, r.y), r.y + r.height),
        );
    };

    const toCanvasXY = (e: ReactMouseEvent<HTMLCanvasElement>) => {
        if(myCanvas.current === null) return {x:-1, y:-1};

        const rect = (myCanvas.current as HTMLCanvasElement).getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    /**
     * A fingertip covers far more than the 8px a mouse cursor does, and on a
     * phone the foot is drawn small enough that neighbouring handles sit a few
     * pixels apart -- so touch gets a bigger radius, and we return the *nearest*
     * handle inside it rather than the first one found.
     */
    const findHandleAt = (cx: number, cy: number, pointerType: string = 'mouse'): DragTarget | null => {
        const R = pointerType === 'touch' ? 24 : 8;
        const R2 = R * R;

        let best: DragTarget | null = null;
        let bestD2 = Infinity;
        const consider = (px: number, py: number, target: DragTarget) => {
            const d2 = (cx - px) ** 2 + (cy - py) ** 2;
            if (d2 <= R2 && d2 < bestD2) {
                bestD2 = d2;
                best = target;
            }
        };

        if (editModeRef.current === 'dimensions') {
            const aS = GetRenderPoint(axisLine.current.start);
            const aE = GetRenderPoint(axisLine.current.end);
            const wS = GetRenderPoint(widthLine.current.start);
            const wE = GetRenderPoint(widthLine.current.end);
            const hS = GetRenderPoint(heelLine.current.start);
            const hE = GetRenderPoint(heelLine.current.end);
            consider(aS.x, aS.y, { type: 'axis', end: 'start' });
            consider(aE.x, aE.y, { type: 'axis', end: 'end' });
            consider(wS.x, wS.y, { type: 'width', end: 'start' });
            consider(wE.x, wE.y, { type: 'width', end: 'end' });
            consider(hS.x, hS.y, { type: 'heel', end: 'start' });
            consider(hE.x, hE.y, { type: 'heel', end: 'end' });
            return best;
        }
        if (editModeRef.current === 'zones' && zones.current) {
            for (let i = 0; i < zones.current.controlPoints.length; i++) {
                const rp = GetRenderPoint(zones.current.controlPoints[i]);
                consider(rp.x, rp.y, { type: 'zone', index: i });
            }
        }
        return best;
    };

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (editModeRef.current === 'none') return;
        const { x, y } = toCanvasXY(e);
        const target = findHandleAt(x, y, e.pointerType);
        if (!target) return;
        dragRef.current = { active: true, target, lastX: x, lastY: y };
        e.preventDefault();
        // Keep receiving moves even once the finger strays outside the canvas.
        e.currentTarget.setPointerCapture?.(e.pointerId);
        editLockScale.current = SSF.current;
        if (target) setCursor('grabbing');
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!dragRef.current.active) return;
        const { x, y } = toCanvasXY(e);
        const dx = x - dragRef.current.lastX;
        const dy = y - dragRef.current.lastY;

        const { ddx, ddy } = canvasDeltaToProcess(dx, dy);

        const applyDeltaToLineEnd = (ln: Line, which: 'start' | 'end') => {
            const cur = which === 'start' ? ln.start : ln.end;
            const moved = clampToROI(new Point(cur.x + ddx, cur.y + ddy));
            if (which === 'start') ln.start = moved; else ln.end = moved;
            ln.calculateDirection?.(ln);
        };

        const tgt = dragRef.current.target!;
        if (tgt.type === 'axis') applyDeltaToLineEnd(axisLine.current, tgt.end);
        if (tgt.type === 'width') applyDeltaToLineEnd(widthLine.current, tgt.end);
        if (tgt.type === 'heel') applyDeltaToLineEnd(heelLine.current, tgt.end);
        if (tgt.type === 'zone' && zones.current) {
            const p = zones.current.controlPoints[tgt.index];
            zones.current.controlPoints[tgt.index] = clampToROI(new Point(p.x + ddx, p.y + ddy));
            zones.current.computeZoneSensors(processROI.current);
        }

        dragRef.current.lastX = x; dragRef.current.lastY = y;
        render();
    };

    /** Hover affordance -- mouse only; a finger has no hover state. */
    const onPointerHover = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (dragRef.current.active || e.pointerType === 'touch') return;
        if (editModeRef.current === 'none') return;
        const { x, y } = toCanvasXY(e);
        setCursor(findHandleAt(x, y, e.pointerType) ? 'grab' : 'default');
    };

    const onPointerUp = () => {
        if (!dragRef.current.active) return;
        dragRef.current.active = false;
        editLockScale.current = null;

        if (editModeRef.current === 'dimensions' && onGeometryChange) {
            const clone = (ln: Line) => {
                const L = new Line();
                L.start = new Point(ln.start.x, ln.start.y);
                L.end = new Point(ln.end.x, ln.end.y);
                L.calculateDirection?.(L);
                return L;
            };
            onGeometryChange({
                kind: 'dimensions',
                axis: clone(axisLine.current),
                width: clone(widthLine.current),
                heel: clone(heelLine.current),
            });
        } else if (editModeRef.current === 'zones' && onGeometryChange && zones.current) {
            onGeometryChange({
                kind: 'zones',
                controlPoints: zones.current.controlPoints.map(p => new Point(p.x, p.y)),
            });
        }
        setCursor('default');
    };

    const canvasDeltaToProcess = (dx: number, dy: number) => {
        const invScaleX = renderROI.current.width / renderRectangle.current.width;
        const invScaleY = renderROI.current.height / renderRectangle.current.height;
        return { ddx: dx * invScaleX, ddy: dy * invScaleY };
    };

    useEffect(() => {
        if (myCanvas.current) myCanvas.current.style.cursor =
            cursor === 'grabbing' ? 'grabbing' :
                cursor === 'grab' ? 'grab' : 'default';
    }, [cursor]);

    const startFrameTimer = () => {
        if (intervalIdRef.current) {
            stopFrameTimer();
        }

        intervalIdRef.current = setInterval(() => {
            currentFrameIndex.current++;
            if (calibFrameArray.current && currentFrameIndex.current > calibFrameArray.current.length - 1) {
                currentFrameIndex.current = 0;
            }
            setSliderValue(currentFrameIndex.current);
            render();
            if (frameTimes.current != null)
                setTimeLabel(frameTimes.current[currentFrameIndex.current] + (' ms'));
            else
                setTimeLabel("0 ms");
        }, 100);
    }

    const stopFrameTimer = () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    };

    const Play = () => {
        if (calibFrameArray.current && calibFrameArray.current.length > 1) {
            setPauseFrames(false);
            startFrameTimer();
            setSliderMaxValue(calibFrameArray.current.length - 1);
            setSliderValue(currentFrameIndex.current);
        }
        else {
            setPauseFrames(true);
            renderRectangle.current = GetZoomRectangle(processROI.current);
            render();
        }
    }

    const Pause = () => {
        stopFrameTimer();
    }

    const playButtonClicked = () => {
        setPauseFrames(p => {
            if (!p) {
                Pause();
            } else {
                Play();
            }
            return !p;
        });
    }

    const GenerateCustomColorMap = (type: string = ''): Promise<[number, number, number][]> => {
        return new Promise((resolve) => {
            ReadPalleteImage((im) => {
                const customLUT: [number, number, number][] = [];
                for (let i = 0; i < 256; i++) {
                    let color = im.ucharPtr(0, 255 - i);
                    let b = color[2];
                    let g = color[1];
                    let r = color[0];
                    customLUT.push([b, g, r]);
                }
                im.delete();
                resolve(customLUT);
            }, type);
        });
    };

    const ReadPalleteImage = (callback: (im: Mat) => void, type: string): void => {
        let img = new Image();
        img.src = printMode || type == 'print' ? config.palletePrintImagePath : config.palleteImagePath;
        img.crossOrigin = "Anonymous";

        img.onload = function () {
            // if (myCanvas.current) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            let ctx = tempCanvas.getContext("2d");
            if (!ctx) return;
            ctx.drawImage(img, 0, 0);

            let srcMat = imread(tempCanvas);

            //Clear canvas after reading Palette
            ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            callback(srcMat);
            // };
        }
    };

    useEffect(() => {
        if (chartVis) {
            updateChartSeries();
        }
    }, [chartVis]);

    const render = async () => {
        if (!colorMapCustom.current || !myCanvas.current || !calibFrameArray.current || SSF.current == 0) return;
       
        try {
            updateBitmap();

            if (!currentBitmap.current) return;

            var afterFilter = await applyFilters(currentBitmap.current);

            if (mode === 'online') {
                drawCOP(afterFilter);
            }
        } catch (error) {
            console.error("Error rendering:", error);
            return;
        }

        let ctx = myCanvas.current.getContext("2d");
        let imageData = convertMatToImageData(afterFilter);
        const bitmap = await createImageBitmap(imageData);
        afterFilter.delete();

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, myCanvas.current.width, myCanvas.current.height);
 
        ctx.drawImage(
            bitmap,
            renderROI.current.x * SSF.current,
            renderROI.current.y * SSF.current,
            renderROI.current.width * SSF.current,
            renderROI.current.height * SSF.current,
            renderRectangle.current.x,
            renderRectangle.current.y,
            (printMode && !isDynamicView) ? renderRectangle.current.width + 150 : renderRectangle.current.width,
            (printMode && !isDynamicView) ? renderRectangle.current.height + 75 : renderRectangle.current.height,
        );

        if (renderOptions.current.Draw_WidthLine) {
            const axisStart = GetRenderPoint(axisLine.current.start);
            const axisEnd = GetRenderPoint(axisLine.current.end);

            const widthStart = GetRenderPoint(widthLine.current.start);
            const widthEnd = GetRenderPoint(widthLine.current.end);

            const heelStart = GetRenderPoint(heelLine.current.start);
            const heelEnd = GetRenderPoint(heelLine.current.end);

            ctx.setLineDash([2, 2]);
            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo(axisStart.x, axisStart.y);
            ctx!.lineTo(axisEnd.x, axisEnd.y);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((axisStart.x + 6), axisStart.y);
            ctx!.lineTo((axisStart.x - 4), axisStart.y);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((axisEnd.x + 6), axisEnd.y);
            ctx!.lineTo((axisEnd.x - 4), axisEnd.y);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((widthStart.x), widthStart.y);
            ctx!.lineTo((widthEnd.x), widthEnd.y);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((heelStart.x), heelStart.y);
            ctx!.lineTo((heelEnd.x), heelEnd.y);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((widthStart.x), widthStart.y + 6);
            ctx!.lineTo((widthStart.x), widthStart.y - 6);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((widthEnd.x), widthEnd.y + 6);
            ctx!.lineTo((widthEnd.x), widthEnd.y - 6);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((heelStart.x), heelStart.y + 6);
            ctx!.lineTo((heelStart.x), heelStart.y - 6);
            ctx!.stroke();

            ctx!.strokeStyle = 'white';
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo((heelEnd.x), heelEnd.y + 6);
            ctx!.lineTo((heelEnd.x), heelEnd.y - 6);
            ctx!.stroke();

            ctx.setLineDash([0, 0]);
        }
        if (renderOptions.current.Draw_ZonesOverlay) {
            DrawZonesOverlay(ctx);
        }
        if (mode !== 'viewer' || renderOptions.current.Draw_COP) {
            if (!calibFrameCOPs.current || calibFrameCOPs.current.length === 0) return;

            const cop = calibFrameCOPs.current[currentFrameIndex.current];
            const copRender = GetRenderPoint(cop);
            if (!copRender) return;
            ctx.strokeStyle = 'blue';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            ctx.stroke();
        }
        if (renderOptions.current.Draw_COP_Line) {
            if (calibFrameCOPs.current && calibFrameCOPs.current.length > 1) {

                var points: Point[] = calibFrameCOPs.current.map(GetRenderPoint).filter((p): p is Point => !!p);

                drawConnectedLines(ctx, points, 4, 'darkred');
                drawConnectedLines(ctx, points, 2, 'orange');

                for (let i = 0; i < points.length; i += 2) {
                    const p = points[i];
                    if (!p) continue;

                    ctx.fillStyle = 'red';
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, 4, 0, 2 * Math.PI);
                    ctx.fill();

                    ctx.fillStyle = 'blue';
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
                    ctx.fill();
                }
            }
        }
        if (false) {
            const left_TOPROI = new cv.Rect(processROI.current.x, processROI.current.y, processROI.current.width, processROI.current.height / 3.0);
            const left_BottomROI = new cv.Rect(processROI.current.x, processROI.current.y + (processROI.current.height * 0.66), processROI.current.width, processROI.current.height / 3.0);

            var HelperCOPs_Left: Point[] = [];
            HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(calibFrameArray.current, left_BottomROI)[0]);
            HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(calibFrameArray.current, left_TOPROI)[0]);

            const step = new cv.Point(-axisLine.current.direction.y, axisLine.current.direction.x);
            const stepM = new cv.Point(axisLine.current.direction.y, -axisLine.current.direction.x);


            // for (var i = 0; i < zones.current?.controlPoints.length; i++) {
            for (var i = 0; i < zones.current?.controlPoints.length; i++) {
                const sensor = zones.current.controlPoints[i];
                // sensor.y -= processROI.current.y;
                // if (calibFrameArray.current[0].ucharPtr(sensor.y, sensor.x)[0] > 0) {
                var coprender = GetRenderPoint(sensor);
                if (!coprender) return;
                ctx.strokeStyle = 'red';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(coprender.x, coprender.y, 3, 0, 2 * Math.PI);
                ctx.stroke();
                // }

                // if (calibMaxFrame.current.ucharPtr(sensor.y, sensor.x)[0] > 16) {
                //     var coprender = getRenderPoint(sensor);
                //     if (!coprender) return;
                //     ctx.strokeStyle = 'blue';
                //     ctx.lineWidth = 2;
                //     ctx.beginPath();
                //     ctx.arc(coprender.x, coprender.y, 3, 0, 2 * Math.PI);
                //     ctx.stroke();
                // }

            }
            // for (let i = 0; i < processROI.current.height; i++) {
            //     const curY2 = processROI.current.y + i;
            //     const basePoint = new cv.Point(axisLine.current.xByY(curY2), curY2);
            //     let curPoint = new cv.Point(axisLine.current.xByY(curY2), curY2);

            //     let curX = Math.round(curPoint.x);
            //     let curY = Math.round(curPoint.y);

            //     let copRender = getRenderPoint(curPoint);
            //     if (!copRender) return;
            //     ctx.strokeStyle = 'red';
            //     ctx.lineWidth = 2;
            //     ctx.beginPath();
            //     ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            //     ctx.stroke();

            //     while (frameProcessor.current.isPointInsideROI({ x: curX, y: curY }, processROI.current) && frameProcessor.current.distancePoint(curPoint, basePoint) < 5) {
            //         curPoint = frameProcessor.current.addPoint(curPoint, step);
            //         curX = Math.round(curPoint.x);
            //         curY = Math.round(curPoint.y);

            //         copRender = getRenderPoint(curPoint);
            //         if (!copRender) return;
            //         ctx.strokeStyle = 'red';
            //         ctx.lineWidth = 2;
            //         ctx.beginPath();
            //         ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            //         ctx.stroke();
            //     }

            //     curPoint = frameProcessor.current.addPoint(basePoint, stepM);
            //     curX = Math.round(curPoint.x);
            //     curY = Math.round(curPoint.y);

            //     while (frameProcessor.current.isPointInsideROI({ x: curX, y: curY }, processROI.current) && frameProcessor.current.distancePoint(curPoint, basePoint) < 5) {
            //         curPoint = frameProcessor.current.addPoint(curPoint, stepM);
            //         curX = Math.round(curPoint.x);
            //         curY = Math.round(curPoint.y);

            //         copRender = getRenderPoint(curPoint);
            //         if (!copRender) return;
            //         ctx.strokeStyle = 'red';
            //         ctx.lineWidth = 2;
            //         ctx.beginPath();
            //         ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            //         ctx.stroke();
            //     }
            // }

            // let copRender = getRenderPoint(HelperCOPs_Left[0]);
            // if (!copRender) return;
            // ctx.strokeStyle = 'blue';
            // ctx.lineWidth = 2;
            // ctx.beginPath();
            // ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            // ctx.stroke();

            // copRender = getRenderPoint(HelperCOPs_Left[1]);
            // if (!copRender) return;
            // ctx.strokeStyle = 'blue';
            // ctx.lineWidth = 2;
            // ctx.beginPath();
            // ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
            // ctx.stroke();
        }

        const drawHandle = (ctx: CanvasRenderingContext2D, p: Point) => {
            // On mobile a 4px dot is both hard to see and hard to aim a finger
            // at, so the grab handles are drawn larger there.
            const r = stackChart ? 7 : 4;
            ctx.fillStyle = 'white';
            ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, 2 * Math.PI); ctx.fill();
            ctx.strokeStyle = 'black'; ctx.lineWidth = stackChart ? 1.5 : 1; ctx.stroke();
        };

        if (editModeRef.current === 'dimensions') {
            drawHandle(ctx, GetRenderPoint(axisLine.current.start));
            drawHandle(ctx, GetRenderPoint(axisLine.current.end));
            drawHandle(ctx, GetRenderPoint(widthLine.current.start));
            drawHandle(ctx, GetRenderPoint(widthLine.current.end));
            drawHandle(ctx, GetRenderPoint(heelLine.current.start));
            drawHandle(ctx, GetRenderPoint(heelLine.current.end));
        }
        if (editModeRef.current === 'zones' && zones.current) {
            zones.current.controlPoints.forEach(pt => drawHandle(ctx, GetRenderPoint(pt)));
        }
    }

    const drawConnectedLines = (ctx: any, points: Point[], width: number, color: string) => {
        ctx.beginPath();

        ctx.strokeStyle = color;
        ctx.lineWidth = width;

        ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.stroke();
    };

    const applyFilters = async (src: Mat, isPressureMesh: boolean = false, type: string = ''): Promise<Mat> => {
        let normalize = Normalize(src.clone(), isPressureMesh);

        let dst5 = new Mat();
        const firstScaleFactor = 5;
        const dsize = new Size(src.cols * firstScaleFactor, src.rows * firstScaleFactor);
        resize(normalize, dst5, dsize, INTER_LINEAR);

        let dstBlur = new Mat();
        GaussianBlur(dst5, dstBlur, new Size(5, 5), 1.4);
        let colorized = new Mat();

        if (type !== 'Black_White') {
            colorized = await ApplyCustomColorMap(dstBlur, type);
        }
        else {
            colorized = dstBlur.clone();
        }

        let dstFinal = new Mat();
        const finalScaleFactor = (editLockScale.current ?? SSF.current);
        const finalDSize = new Size(src.cols * finalScaleFactor, src.rows * finalScaleFactor);
        resize(colorized, dstFinal, finalDSize, INTER_LINEAR);

        normalize.delete();
        dst5.delete();
        dstBlur.delete();
        colorized.delete();

        return dstFinal;
    }

    const Normalize = (img: Mat, isPressureMesh: boolean): Mat => {
        let result = minMaxLoc(img);
        let maxVal = result.maxVal;
        if (maxVal < 111) maxVal = 111;

        let normalize = new Mat();
        const scaleValue = isPressureMesh ? 1 : 255 / maxVal;
        img.convertTo(normalize, CV_8U, scaleValue);
        img.delete();
        return normalize;
    }

    const ApplyCustomColorMap = async (srcMat: Mat, type: string): Promise<Mat> => {
        let dst = new Mat();
        // console.log(srcMat);
        cvtColor(srcMat, dst, COLOR_GRAY2BGR);
        // console.log(srcMat);

        if (colorMapPrint.current == null || colorMapCustom.current == null) {
            await waitForColorMap(() => colorMapCustom.current !== null);
        }

        for (let r = 0; r < srcMat.rows; r++) {
            for (let c = 0; c < srcMat.cols; c++) {
                let pixelValue = srcMat.ucharAt(r, c);
                let color = type === 'print' ? colorMapPrint.current[pixelValue] : colorMapCustom.current[pixelValue];
                dst.ucharPtr(r, c)[0] = color[0]; //blue
                dst.ucharPtr(r, c)[1] = color[1]; //green
                dst.ucharPtr(r, c)[2] = color[2]; //red
            }
        }
        return dst;
    }

    function waitForColorMap(checkCondition: () => boolean, interval = 50) {
        return new Promise(resolve => {
            const check = () => {
                if (checkCondition()) {
                    resolve(true);
                } else {
                    setTimeout(check, interval);
                }
            };

            check();
        });
    }

    const drawCOP = (mat: Mat): void => {
        if (!mat || !calibFrameArray.current || calibFrameArray.current.length === 0) return;

        const cop = frameProcessor.current.ComputeCOP(calibFrameArray.current[currentFrameIndex.current]);
        const copRender = GetRenderPoint(cop);
        if (!copRender) return;
        circle(
            mat,
            copRender,
            3,
            [204, 136, 0, 255],
            2,
            LINE_AA
        );
    }

    const GetRenderPoint = (point: Point) => {
        const scaleX = renderRectangle.current.width / renderROI.current.width;
        const scaleY = renderRectangle.current.height / renderROI.current.height;

        let x = (point.x - renderROI.current.x) * scaleX + renderRectangle.current.x;
        let y = (point.y - renderROI.current.y) * scaleY + renderRectangle.current.y;


        if (x > renderRectangle.current.x + renderRectangle.current.width) {
            x = renderRectangle.current.x + renderRectangle.current.width;
        } else if (x < renderRectangle.current.x) {
            x = renderRectangle.current.x;
        }

        if (y > renderRectangle.current.y + renderRectangle.current.height) {
            y = renderRectangle.current.y + renderRectangle.current.height;
        } else if (y < renderRectangle.current.y) {
            y = renderRectangle.current.y
        }

        return new cv.Point(x, y);
    }

    const GetZoomRectangle = (roi: Rect): Rect => {
        const rect = myCanvas.current.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = rect.height;

        // Update canvas dimensions to match display size
        myCanvas.current.width = displayWidth;
        myCanvas.current.height = displayHeight;
    
        const targetWidthRatio = 1;
        const targetHeightRatio = 1;

        const scaleX = (displayWidth * targetWidthRatio) / roi.width;
        const scaleY = (displayHeight * targetHeightRatio) / roi.height;

        SSF.current = Math.min(scaleX, scaleY);

        const newWidth = Math.round(SSF.current * roi.width);
        const newHeight = Math.round(SSF.current * roi.height);

        const locx = Math.round((displayWidth - newWidth) / 2.0);
        const locy = Math.round((displayHeight - newHeight) / 2.0);

        return new Rect(locx, locy, newWidth, newHeight);
    }

    const convertMatToImageData = (imgMat: Mat): ImageData => {
        let rgbaMat = new Mat();
        cvtColor(imgMat, rgbaMat, COLOR_BGR2RGBA);
        let imgData = new ImageData(new Uint8ClampedArray(rgbaMat.data), rgbaMat.cols, rgbaMat.rows);
        rgbaMat.delete();
        return imgData;
    }

    const detectFoot = () => {
        return frameProcessor.current?.getSumOfFrame(calibFrameArray.current[currentFrameIndex.current]) > 500;
    }

    const setZones = (processedZones: zoneProcessor) => {
        zones.current = zoneProcessor.cloneFrom(processedZones);
        zones.current.computeZoneSensors(processROI.current);
    }

    const setFrameTimes = (times: number[]) => {
        currentFrameIndex.current = 0;
        frameTimes.current = times;
    }

    const setCalibFrameArray = (frames: Mat[]) => {
        if (frames == null) return;

        currentFrameIndex.current = 0;
        calibFrameArray.current = frames.slice();

        if (renderOptions.current.FullView) processROI.current = new Rect(0, 0, frames[0].cols, frames[0].rows);

        let maxValue = frameProcessor.current.getMaxValue(frameProcessor.current.getMaxFrame(calibFrameArray.current));
        if (maxValue < 111) maxValue = 111;
        setMaxRenderValue(maxValue);

        renderRectangle.current = GetZoomRectangle(processROI.current);
    }

    const setCalibMaxFrame = (frame: Mat) => {
        if (frame != null)
            calibMaxFrame.current = frame.clone();
        else
            calibMaxFrame.current = null;
    }

    const setCropView = () => {
        renderROI.current = new Rect(processROI.current.x, processROI.current.y, processROI.current.width, processROI.current.height);
        renderRectangle.current = GetZoomRectangle(renderROI.current);
        render();
    }

    const updateBitmap = () => {
        if (renderOptions.current.Render_MaxFrame)
            currentBitmap.current = calibMaxFrame.current ? calibMaxFrame.current.clone() : null;
        else
            currentBitmap.current = calibFrameArray.current ? calibFrameArray.current[currentFrameIndex.current].clone() : null;
    }

    const getZoneCenter = (zname: ZoneNames) => {
        var zc = zones.current?.GetZoneCenteroid(zname);

        const zerox = processROI.current.x + processROI.current.width;
        const zeroy = processROI.current.y;

        const center = new cv.Point((zerox - zc.x) * sensorWidth.current + sensorWidth.current / 2.0, (zc.y - zeroy) * sensorHeight.current + sensorHeight.current / 2.0);

        return center;
    }

    const setStaticView = () => {
        setIsDynamicView(false);
        Pause();
    }

    const setDynamicView = () => {
        setIsDynamicView(true);
        Pause();
    }

    const updateChartSeries = () => {
        if (!calibFrameArray.current) return;

        if (currentChart == 'force') {
            var force: number[] = [];
            var pressure: number[] = [];
            var surface: number[] = [];
            var ZoneSensors = frameProcessor.current.GetAllROISensors(processROI.current);

            for (var i = 0; i < calibFrameArray.current.length; i++) {
                var ZoneSurface = frameProcessor.current.GetSurface(ZoneSensors, calibFrameArray.current[i]);
                var Pressure = frameProcessor.current.GetAveragePressure(ZoneSensors, calibFrameArray.current[i]);
                var Force = frameProcessor.current.GetSumPressure(ZoneSensors, calibFrameArray.current[i]) * frameProcessor.current.GetForcePressureRatio();
                force.push(Force);
                pressure.push(Pressure);
                surface.push(ZoneSurface);
            }

            const forceDictionary = { 'Force(N)': force, 'Pressure(kg/cm²)': pressure, 'Surface(cm²)': surface };
            setChartTimes(frameTimes.current ? frameTimes.current.slice() : []);
            setChartData(forceDictionary);

            return forceDictionary;
        }
        else if (currentChart == 'zones') {
            let zonesDictionary: { [key: string]: number[] } = {};

            for (const key in ZoneNames) {
                zonesDictionary[`${key}(N)`] = [];
            }

            for (var i = 0; i < calibFrameArray.current.length; i++) {
                for (const zone of Object.values(ZoneNames)) {
                    let ZoneSensors = zones.current?.GetZoneSensors(zone, processROI.current);
                    if (!ZoneSensors) continue;

                    var Pressure = frameProcessor.current.GetAveragePressure(ZoneSensors, calibFrameArray.current[i]);
                    var Force = Math.round(Pressure * frameProcessor.current.GetForcePressureRatio() * 100) / 100;

                    zonesDictionary[`${zone}(N)`]?.push(Force);
                }
            }
            setChartTimes(frameTimes.current ? frameTimes.current.slice() : []);
            setChartData(zonesDictionary);

            return zonesDictionary;
        }
        else if (currentChart == 'cursors') {
            setChartTimes(frameTimes.current ? frameTimes.current.slice() : []);
            setChartData({ cursor: [] });
        }

        return null;
    }

    const getHeelCenterOfPressure = () => {
        const zerox = processROI.current.x + processROI.current.width;
        const zeroy = processROI.current.y;
        const ZoneSensors = zones.current?.GetZoneSensors(ZoneNames.HL, processROI.current);
        if (ZoneSensors && zones.current) {
            const hmSensors = zones.current.GetZoneSensors(ZoneNames.HM, processROI.current);
            if (hmSensors) ZoneSensors.push(...hmSensors);
        }
        const zc = frameProcessor.current?.GetCenterOfPressure(ZoneSensors, calibMaxFrame.current, 0.5);

        const center = new cv.Point((zerox - zc.x) * sensorWidth.current + sensorWidth.current / 2.0, (zc.y - zeroy) * sensorHeight.current + sensorHeight.current / 2.0);

        return center;
    }

    const getZonePressure = (zname: ZoneNames) => {
        const ZoneSensors = zones.current?.GetZoneSensors(zname, processROI.current);
        return frameProcessor.current?.GetSumPressure(ZoneSensors, calibMaxFrame.current);
    }

    const getZoneCenterOfPressureMinThr = (zname: ZoneNames, thr: number) => {
        const zerox = processROI.current.x + processROI.current.width;
        const zeroy = processROI.current.y;
        const ZoneSensors = zones.current?.GetZoneSensors(zname, processROI.current);

        const zc = frameProcessor.current?.GetCenterOfPressureMinThr(ZoneSensors, calibMaxFrame.current, 0.95, thr);
        const center = new cv.Point((zerox - zc.x) * sensorWidth.current, (zc.y - zeroy) * sensorHeight.current);

        return center;
    }

    const setPressureData = (roi: Rect, processCOPs: Point[], axis: Line, width: Line, heel: Line) => {
        processROI.current = roi;
        calibFrameCOPs.current = processCOPs.slice();
        axisLine.current = new Line(axis.start, axis.end);
        widthLine.current = new Line(width.start, width.end);
        heelLine.current = new Line(heel.start, heel.end);
    }

    const setProcessData = (sensorwidth: number, sensorheight: number, fp: FrameProcessorPressure) => {
        sensorWidth.current = sensorwidth;
        sensorHeight.current = sensorheight;
        frameProcessor.current = fp;
    }

    const setRenderOptions = (options: PressureRenderOptions) => {
        renderOptions.current = options.copy();
        render();
    }

    const getRealSizedPoint = (framePoint: Point) => {
        const zerox = processROI.current.x + processROI.current.width;
        const zeroy = processROI.current.y;
        const center = new Point((zerox - framePoint.x) * sensorWidth.current, (framePoint.y - zeroy) * sensorHeight.current);
        ctrlpnt.current.push(center);
        return center;
    }

    const exportBitmap = (mat?: Mat, filename: string = "pressure_scan.png") => {
        if (!mat) {
            return;
        }
        try {
            // Convert Mat to canvas
            const canvas = document.createElement("canvas");
            canvas.width = mat.cols;
            canvas.height = mat.rows;
            cv.imshow(canvas, mat);

            // Create a link and trigger download
            canvas.toBlob((blob) => {
                if (!blob) {
                    return;
                }
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            }, "image/png");
        } catch (err) {
            console.error("Export bitmap error:", err);
        }
    };

    const getPrintBitmap = async (width: number, height: number, pmode: string): Promise<HTMLCanvasElement | null> => {
        // Create an offscreen canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        const oldrect = renderRectangle.current;
        const TempOptions = renderOptions.current.copy();

        renderRectangle.current = pmode == 'MMPixel'
            ? new Rect(0, 0, width, height)
            : GetFixZoomRectangle(renderROI.current, width, height);

        // Set rendering options based on pmode
        renderOptions.current.Render_MaxFrame = true;
        updateBitmap();

        if (pmode == 'MMPixel') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = false;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = false;
            renderOptions.current.Draw_AxisLine = false;
            renderOptions.current.Draw_WidthLine = false;
            renderOptions.current.Draw_HeelLine = false;
            renderOptions.current.Draw_COP = false;
            renderOptions.current.Draw_ProcessCOPs = false;
            //renderOptions.current.Draw_ProcessCOPLINE = false;
        }
        if (pmode == 'Pressure_Mesh') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = false;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = false;
            renderOptions.current.Draw_AxisLine = false;
            renderOptions.current.Draw_WidthLine = false;
            renderOptions.current.Draw_HeelLine = false;
            renderOptions.current.Draw_COP = false;
            renderOptions.current.Draw_ProcessCOPs = false;
            renderOptions.current.Draw_COP_Line = true;
            renderOptions.current.Draw_ProcessROI = false;
        }
        if (pmode === 'Dynamic_Zone') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = true;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = true;
            renderOptions.current.Zones_Alpha = 50;
            renderOptions.current.Draw_Zone_Sensors = true;
            renderOptions.current.Draw_COP_Line = false;
            renderOptions.current.Draw_COP = false;
            renderOptions.current.Draw_AxisLine = false;
            renderOptions.current.Draw_WidthLine = false;
            renderOptions.current.Draw_HeelLine = false;
            renderOptions.current.Draw_ProcessCOPs = false;
        }
        if (pmode === 'Dynamic_Axis') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = true;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = false;
            renderOptions.current.Draw_Zone_Sensors = false;
        }
        if (pmode === 'Static_FootDimension') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = true;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = false;
        }
        if (pmode === 'Dynamic_Auto' || pmode === 'Black_White') {
            renderOptions.current.Draw_Cursors = false;
            renderOptions.current.Draw_CursorSensor = false;
            renderOptions.current.Draw_Measurements = false;
            renderOptions.current.Draw_ZonesOverlay = false;
            renderOptions.current.Draw_Zone_FillPolygons = false;
            renderOptions.current.Draw_Zone_Name = false;
            renderOptions.current.Draw_AxisLine = false;
            renderOptions.current.Draw_WidthLine = false;
            renderOptions.current.Draw_HeelLine = false;
            renderOptions.current.Draw_COP = false;
            renderOptions.current.Draw_ProcessCOPs = false;
            renderOptions.current.Draw_COP_Line = true;
        }

        // Draw the bitmap
        if (currentBitmap.current != null) {
            editLockScale.current = 20;
            var afterFilter = await applyFilters(currentBitmap.current.clone(), pmode == 'Pressure_Mesh', pmode === 'Dynamic_Auto' ? '' : pmode === 'Black_White' ? 'Black_White' : 'print');

            // Convert to ImageData and draw on canvas
            let imageData = convertMatToImageData(afterFilter);
            const bitmap = await createImageBitmap(imageData);
            afterFilter.delete();

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(
                bitmap,
                renderROI.current.x * editLockScale.current,
                renderROI.current.y * editLockScale.current,
                renderROI.current.width * editLockScale.current,
                renderROI.current.height * editLockScale.current,
                renderRectangle.current.x,
                renderRectangle.current.y,
                renderRectangle.current.width,
                renderRectangle.current.height,
            );
            // ctx.putImageData(imageData, 0, 0);

            if (renderOptions.current.Draw_ZonesOverlay) {
                if (zones.current != null) {
                    DrawZonesOverlay(ctx);
                }
            }
            if (renderOptions.current.Draw_AxisLine) {
                const axisStart = GetRenderPoint(axisLine.current.start);
                const axisEnd = GetRenderPoint(axisLine.current.end);

                ctx.setLineDash([2, 2]);
                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo(axisStart.x, axisStart.y);
                ctx!.lineTo(axisEnd.x, axisEnd.y);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((axisStart.x + 6), axisStart.y);
                ctx!.lineTo((axisStart.x - 4), axisStart.y);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((axisEnd.x + 6), axisEnd.y);
                ctx!.lineTo((axisEnd.x - 4), axisEnd.y);
                ctx!.stroke();
                ctx.setLineDash([0, 0]);
            }
            if (renderOptions.current.Draw_WidthLine) {
                const widthStart = GetRenderPoint(widthLine.current.start);
                const widthEnd = GetRenderPoint(widthLine.current.end);

                ctx.setLineDash([2, 2]);
                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((widthStart.x), widthStart.y);
                ctx!.lineTo((widthEnd.x), widthEnd.y);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((widthStart.x), widthStart.y + 6);
                ctx!.lineTo((widthStart.x), widthStart.y - 6);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((widthEnd.x), widthEnd.y + 6);
                ctx!.lineTo((widthEnd.x), widthEnd.y - 6);
                ctx!.stroke();
                ctx.setLineDash([0, 0]);
            }
            if (renderOptions.current.Draw_HeelLine) {
                const heelStart = GetRenderPoint(heelLine.current.start);
                const heelEnd = GetRenderPoint(heelLine.current.end);

                ctx.setLineDash([2, 2]);
                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((heelStart.x), heelStart.y);
                ctx!.lineTo((heelEnd.x), heelEnd.y);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((heelStart.x), heelStart.y + 6);
                ctx!.lineTo((heelStart.x), heelStart.y - 6);
                ctx!.stroke();

                ctx!.strokeStyle = 'black';
                ctx!.lineWidth = 1;
                ctx!.beginPath();
                ctx!.moveTo((heelEnd.x), heelEnd.y + 6);
                ctx!.lineTo((heelEnd.x), heelEnd.y - 6);
                ctx!.stroke();
                ctx.setLineDash([0, 0]);
            }

            if (renderOptions.current.Draw_COP_Line) {
                if (calibFrameCOPs.current && calibFrameCOPs.current.length > 1) {

                    var points: Point[] = calibFrameCOPs.current.map(GetRenderPoint).filter((p): p is Point => !!p);

                    drawConnectedLines(ctx, points, 4, pmode === 'Black_White' ? '#2A2A2A' : 'darkred');
                    drawConnectedLines(ctx, points, 2, pmode === 'Black_White' ? '#AEAEAE' : 'orange');

                    if (renderOptions.current.Draw_ProcessCOPs) {
                        for (let i = 0; i < points.length; i += 2) {
                            const p = points[i];
                            if (!p) continue;

                            ctx.fillStyle = 'red';
                            ctx.beginPath();
                            ctx.arc(p.x, p.y, 4, 0, 2 * Math.PI);
                            ctx.fill();

                            ctx.fillStyle = 'blue';
                            ctx.beginPath();
                            ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
                            ctx.fill();
                        }
                    }
                }
            }

            if (renderOptions.current.Draw_COP) {
                const cop = calibFrameCOPs.current[currentFrameIndex.current];
                const copRender = GetRenderPoint(cop);
                ctx.strokeStyle = 'blue';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(copRender.x, copRender.y, 3, 0, 2 * Math.PI);
                ctx.stroke();
            }
        }

        renderRectangle.current = oldrect;
        renderOptions.current = TempOptions.copy();

        // const pngDataUrl = canvas.toDataURL('image/png');
        // // Trigger download
        // const link = document.createElement('a');
        // link.href = pngDataUrl;
        // link.download = 'pressure-viewer.png';
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
        return canvas;
    }

    function GetFixZoomRectangle(ROI: Rect, Width: number, Height: number): Rect {
        const scaleX = Width / (ROI.width);
        const scaley = Height / (ROI.height);
        const scale = Math.min(scaleX, scaley);

        const NewWidth = scale * (ROI.width);
        const NewHeight = scale * (ROI.height);

        const locx = (Width - NewWidth) / 2.0;
        const locy = (Height - NewHeight) / 2.0;
        return new Rect(locx, locy, NewWidth, NewHeight);
    }

    function DrawZonesOverlay(ctx: CanvasRenderingContext2D) {
        if (!zones.current || !zones.current.controlPoints) {
            return;
        }

        if (zones.current.controlPoints.length === 21) {
            const ZoneTypes = Object.values(ZoneNames);

            for (const zoneNameOrId of ZoneTypes) {
                const z_Points = zones.current.GetZonePolygon(zoneNameOrId);

                if (renderOptions.current.Draw_Zone_Sensors) {
                    if (zones.current.zoneSensorPositions) {
                        const zonepoints = zones.current.zoneSensorPositions[zoneNameOrId as unknown as number];

                        const VisiblePoints = frameProcessor.current.GetVisibleZoneSensors(calibMaxFrame.current, zonepoints);
                        const z0sensors = VisiblePoints.map(p => GetRenderPoint(p));

                        const fillStyle = zones.current.GetFillStyle(zoneNameOrId, 200);
                        ctx.fillStyle = fillStyle;

                        const radius = 4;
                        for (const pnt of z0sensors) {
                            ctx.fillRect(
                                pnt.x - radius,
                                pnt.y - radius,
                                radius * 2,
                                radius * 2
                            );
                        }
                    }
                }

                if (z_Points && z_Points.length > 0) {
                    const z_RenderPoints = z_Points.map(p => GetRenderPoint(p));

                    ctx.beginPath();
                    ctx.moveTo(z_RenderPoints[0].x, z_RenderPoints[0].y);
                    for (let i = 1; i < z_RenderPoints.length; i++) {
                        ctx.lineTo(z_RenderPoints[i].x, z_RenderPoints[i].y);
                    }
                    ctx.closePath();

                    if (renderOptions.current.Draw_Zone_FillPolygons) {
                        ctx.fillStyle = zones.current.GetFillStyle(zoneNameOrId, renderOptions.current.Zones_Alpha ?? 255);
                        ctx.fill();
                    }

                    ctx.strokeStyle = 'white';
                    ctx.lineWidth = 0.3;
                    ctx.stroke();

                    if (renderOptions.current.Draw_Zone_Name) {
                        const CenterPoint = GetRenderPoint(zones.current.GetZoneCenteroid(zoneNameOrId));
                        const zoneName = zoneNameOrId.toString();

                        ctx.font = "5px";
                        ctx.fillStyle = "white";
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(zoneName, CenterPoint.x, CenterPoint.y);
                    }
                }
            }
        }
    }

    const styles = {
        paletteImage: {
            maxWidth: '60%',
            width: '1px',
            height: '6px',
            transform: 'rotate(180deg)',
            marginLeft: '5px',
            marginRight: '5px',

        } as React.CSSProperties,

        canvas: {
            width: '100%',
            height: 'auto',
            border: printMode ? null : '2px solid #0088CC',
            borderRadius: '4px',
        } as React.CSSProperties
    }


    return (
        <>
            { mode !== 'viewer' && !printMode && 
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, justifyContent: 'space-around', padding: '5px' }}>
                    {
                        footLabel 
                            ? <Typography variant="caption" sx={{ color: '#3498db', minWidth: '11%', flexShrink: 0 }}>{footLabel}</Typography>
                            : <Typography variant="caption" className="pressure-heatmap-legend-text">0 (Kg/CM2)</Typography>
                    }
                    <img style={{...styles.paletteImage, flex: 1}} src={config.palleteImagePath} />
                    <Typography variant="caption" className="pressure-heatmap-legend-text">{pressureLabel}</Typography>
                </Box>
            }
            {legendVis && <Box sx={{ width: '85%', height: '10%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <img style={{...styles.paletteImage, flex: 1}} src={config.palleteImagePath} />
                <Typography variant="subtitle1" sx={{ color: '#3498db', minWidth: '13%', fontSize: '8px', textAlign: 'right' }}>{pressureLabel}</Typography>
            </Box>}
            {!printMode && <Box sx={{
                display: 'flex',
                gap: stackChart ? 1 : 2,
                flexDirection: (isDynamicView && !stackChart) ? 'row' : 'column',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                ...(stackChart
                    ? { height: 'auto', flex: 'none' }
                    : { height: '1px', minHeight: 0, flex: 1 }),
            }}>
                <Box sx={{ backgroundColor: '#282a2e',
                    width: (isDynamicView && chartVis && editMode === 'none' && !stackChart) ? '25%' : '100%',
                    border: '4px solid rgba(var(--accent-color-rgb), 0.4)', borderRadius: '10px', overflow: 'hidden',
                    height: stackChart
                        ? (editMode !== 'none' ? 340 : (isDynamicView && chartVis ? 190 : 260))
                        : '100%',
                    flexShrink: 0,
                    display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                    <canvas
                        id="canvas"
                        ref={myCanvas}
                        style={{
                            ...styles.canvas,
                            flex: 1,
                            minHeight: 0,
                            // While editing the drag is ours; without this the
                            // browser claims the touch to scroll the modal and
                            // no move events ever reach us.
                            touchAction: editMode === 'none' ? 'auto' : 'none',
                        }}
                        onPointerDown={onPointerDown}
                        onPointerMove={(e) => { onPointerMove(e); onPointerHover(e); }}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    />
                </Box>
                {isDynamicView && chartVis && editMode === 'none' &&
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', minWidth: 0,
                        ...(stackChart
                            ? { width: '100%', height: 'auto', flexGrow: 0 }
                            : { height: '100%', flexGrow: 1 }),
                        borderRadius: 2,
                        '& .MuiFormControlLabel-label': { fontSize: 11, whiteSpace: 'nowrap' },
                        '& .MuiFormControlLabel-root': { m: 0, minHeight: 10, maxHeight: 18 },
                        '& .MuiCheckbox-root': { p: 0.5 },
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                    }}>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue="force"
                            name="radio-buttons-group"
                            row
                            sx={{ mb: stackChart ? 0 : 1, color: 'var(--text-color)', justifyContent: stackChart ? 'center' : undefined }}
                            value={currentChart}
                            onChange={e => setCurrentChart(e.target.value)}
                        >
                            <FormControlLabel value="force" control={<Radio />} label="Force" />
                            <FormControlLabel value="zones" control={<Radio />} label="Zones" />
                            {/* <FormControlLabel value="cursors" control={<Radio />} label="Cursors" /> */}
                        </RadioGroup>
                        <ChartsWithMUIX
                            chartData={chartData}
                            chartTimesData={chartTimes}
                            density="normal"
                            fillWidth={stackChart}
                            height={stackChart ? 170 : 250}
                        />
                    </Box>}
            </Box>}
            {mode === 'printStatic' && <Box sx={{ padding: '5px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: '90%', ml: 2 }}>
                <canvas id="canvas" ref={myCanvas} style={{...styles.canvas, height: '100%'}} />
            </Box>
            }
            {mode === 'printDynamic' && <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', flex: 1, minHeight: 0 }}>
                {!chartVis
                    ? <Box sx={{ width: '100%', height: '100%', mt: 5, marginTop: 0 }}>
                        <canvas id="canvas" ref={myCanvas} style={{...styles.canvas, height: '100%'}} />
                    </Box>
                    : <Box sx={{ width: '35%', height: '100%' }}>
                        <canvas id="canvas" ref={myCanvas} style={{...styles.canvas, height: '100%'}} />
                    </Box>}
                {chartVis &&
                    <Box sx={{ width: 450, height: 200 }}>
                        <ChartsWithMUIX
                            width={380}
                            height={200}
                            xLabel="Time (ms)"
                            yLabel="Force (N)"
                            density="compact"
                            visPanelWidth={50}
                            chartData={chartData}
                            chartTimesData={chartTimes} />
                    </Box>}
            </Box>}
            {isDynamicView && (editMode === 'none') && <Stack spacing={2} direction="row" sx={{ mt: 1, alignItems: 'center', width: '100%' }} >
                <Button variant="contained" onClick={playButtonClicked} sx={{ minWidth: '40px' }} >
                    {pauseFrames ?
                        <PlayArrowIcon sx={{ fontSize: 16 }} /> :
                        <PauseIcon sx={{ fontSize: 16 }} />
                    }
                </Button>
                <input type="range"
                    style={{ width: '100%', marginLeft: '10px' }}
                    aria-label="Volume"
                    value={sliderValue}
                    onChange={e => handleSliderChange(e.target.value)}
                    min={0}
                    max={sliderMaxValue}
                />
                <Typography variant="subtitle1" sx={{ color: '#3498db', minWidth: '8%', fontSize: '12px', textAlign: 'center' }}>{timeLabel}</Typography>
            </Stack>}
        </>
    );
});

export default PressureViewerComponent;

function useResizeObserver(elementRef: any) {
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const currentElement = elementRef.current;

        // Check if the ref is connected to the DOM
        if (!currentElement || typeof ResizeObserver === 'undefined') {
            return;
        }

        // 1. Initialize dimensions
        const initialRect = currentElement.getBoundingClientRect();
        setDimensions({ width: initialRect.width, height: initialRect.height });

        // 2. Setup observer
        const observer = new ResizeObserver((entries) => {
            if (entries[0] && entries[0].contentRect) {
                const { width, height } = entries[0].contentRect;
                setDimensions({ width, height });
            }
        });

        // Start observing the provided element
        observer.observe(currentElement);

        // 3. Cleanup: unobserve and disconnect
        return () => {
            observer.unobserve(currentElement);
            observer.disconnect();
        };

        // Dependency array ensures this effect runs ONLY when the ref's current value changes.
        // However, since refs are stable, it mostly runs only on mount.
    }, [elementRef]);

    return dimensions;
}
